import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    InputAdornment,
    Select,
    MenuItem,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    FormControl,
    InputLabel,
    Pagination,
    CircularProgress,
    Tooltip,
    alpha,
    IconButton,
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterListIcon,
    CalendarMonth as CalendarIcon,
    Person as PersonIcon,
    MonitorHeart as ActivityIcon,
    ErrorOutline as AlertCircleIcon,
    FileDownload as FileDownloadIcon,
    History as HistoryIcon,
    RestartAlt as ResetIcon,
} from '@mui/icons-material';
import AuditLogSvgIcon from '../components/icons/AuditLogSvgIcon';
import auditLogService, { AuditLog } from '../services/auditLogService';
import userService from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { RoleChip } from './SettingsConfigPage';
import { formatToUIDateTimeLong } from '../utils/dateUtils';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';

const robustFormat = (d: any): string => {
    if (!d) return "Unknown";
    let s = d;
    // If it looks like an ISO date (has T and dashes) but lacks the Z suffix,
    // we assume the backend sent a raw UTC string and we append Z to trigger correct local conversion.
    if (typeof d === 'string' && !d.endsWith('Z') && (d.includes('T') || d.includes('-'))) {
        s = d.includes(' ') && !d.includes('T') ? d.replace(' ', 'T') + 'Z' : d + 'Z';
    }
    return formatToUIDateTimeLong(s);
};

const parseLogTime = (d: any) => {
    if (!d) return 0;
    let s = d;
    if (typeof s === 'string' && !s.endsWith('Z') && (s.includes('T') || s.includes('-'))) {
        s = s.includes(' ') && !s.includes('T') ? s.replace(' ', 'T') + 'Z' : s + 'Z';
    }
    return new Date(s).getTime() || 0;
};

const AuditLogsPage: React.FC = () => {
    const { user } = useAuth();
    const { tenant } = useTenant();
    const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [exportLoading, setExportLoading] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedAction, setSelectedAction] = useState('');
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const [statFilter, setStatFilter] = useState<'ALL' | 'TODAY' | 'USERS' | 'FAILED'>('ALL');


    // Pagination
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [uniqueUsersCount, setUniqueUsersCount] = useState<number | string>('...');
    const [todayActionsCount, setTodayActionsCount] = useState<number | string>('...');
    const [failedAttemptsCount, setFailedAttemptsCount] = useState<number | string>('...');
    const [todaysAuditCount, setTodaysAuditCount] = useState<number | string>('...');
    const [allUserNames, setAllUserNames] = useState<string[]>([]);

    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        const fetchUniqueUsersCount = async () => {
            try {
                const res = await userService.getUsersCount();
                let userCountVal = 'N/A';
                let todayVal = 'N/A';
                let failedVal = 'N/A';

                if (typeof res === 'number' || typeof res === 'string') {
                    userCountVal = String(res);
                } else if (res && typeof res === 'object') {
                    const obj = res as any;
                    userCountVal = String(obj.activeUserCount ?? obj.count ?? obj.total ?? obj.data ?? JSON.stringify(res));
                    if (obj.todayAuditLogCount !== undefined) {
                        todayVal = String(obj.todayAuditLogCount);
                    }
                    if (obj.failedAuditLogCount !== undefined) {
                        failedVal = String(obj.failedAuditLogCount);
                    }
                }
                setUniqueUsersCount(userCountVal);

                // We get today and failed counts from auditLogService.getAll() instead.
            } catch (error) {
                console.error("Failed to fetch unique users count:", error);
                setUniqueUsersCount('N/A');
            }
        };

        const fetchAllUserNames = async () => {
            try {
                const response = await userService.getActiveUsers();
                if (response && Array.isArray(response)) {
                    const names = response.map(u => {
                        if (u.fullName) return u.fullName;
                        if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`;
                        if (u.firstName) return u.firstName;
                        if (u.lastName) return u.lastName;
                        return u.username;
                    }).filter(Boolean);
                    setAllUserNames(Array.from(new Set(names)).sort());
                }
            } catch (error) {
                console.error("Failed to fetch all users:", error);
            }
        };

        fetchUniqueUsersCount();
        fetchAllUserNames();

        const fetchTodaysAuditCount = async () => {
            try {
                const data = await auditLogService.getAll({ page: 0, size: 1, fromDate: today, toDate: today });
                const countValue = data.totalElements ?? data.auditLogCount ?? data.todayAuditLogCount ?? 0;
                setTodaysAuditCount(typeof countValue === 'number' ? countValue : Number(countValue) || 0);
            } catch (error) {
                console.error('Failed to fetch today audit count:', error);
                setTodaysAuditCount('N/A');
            }
        };

        fetchTodaysAuditCount();
    }, []);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                setLoading(true);
                const params: any = {
                    page: 0,
                    size: 2000,
                };

                if (searchQuery) {
                    params.search = searchQuery;
                }

                if (dateFrom && !dateTo) {
                    params.fromDate = format(dateFrom, 'yyyy-MM-dd');
                    params.toDate = format(new Date(), 'yyyy-MM-dd');
                    params.sort = 'actionTimestamp,asc';
                } else if (dateFrom && dateTo) {
                    params.fromDate = format(dateFrom, 'yyyy-MM-dd');
                    params.toDate = format(dateTo, 'yyyy-MM-dd');
                    params.sort = 'actionTimestamp,asc';
                } else if (!dateFrom && dateTo) {
                    params.toDate = format(dateTo, 'yyyy-MM-dd');
                }

                let allLogs: any[] = [];
                let currentPage = 0;
                let totalPages = 1;
                let finalData: any = null;

                while (currentPage < totalPages) {
                    params.page = currentPage;
                    params.size = 2000;

                    const data = await auditLogService.getAll(params);

                    if (currentPage === 0) {
                        finalData = data;
                        totalPages = data.totalPages;
                    }

                    if (data.content && data.content.length > 0) {
                        allLogs = [...allLogs, ...data.content];
                    } else {
                        break;
                    }

                    currentPage++;
                    if (currentPage >= 15) break; // Safety limit
                }

                setAuditLogs(allLogs);

                if (finalData) {
                    setTotalPages(finalData.totalPages);
                    setTotalElements(finalData.totalElements);

                    if (finalData.todayAuditLogCount !== undefined) {
                        setTodayActionsCount(finalData.todayAuditLogCount.toString());
                    } else if (finalData.auditLogCount !== undefined) {
                        setTodayActionsCount(finalData.auditLogCount.toString());
                    }
                    if (finalData.failedAuditLogCount !== undefined) {
                        setFailedAttemptsCount(finalData.failedAuditLogCount.toString());
                    }
                }
            } catch (err) {
                console.error("Error fetching audit logs:", err);
                setError("Failed to load audit logs.");
            } finally {
                setLoading(false);
            }
        };

        const timer = setTimeout(() => {
            fetchLogs();
        }, 500);

        return () => clearTimeout(timer);
    }, [dateFrom, dateTo, searchQuery]);

    // Filter Logic resets page
    useEffect(() => {
        setPage(1);
    }, [searchQuery, selectedUser, selectedAction, dateFrom, dateTo, statFilter, tenant]);

    const filteredLogs = auditLogs.filter(log => {

        const matchesUser = selectedUser ? log.userName === selectedUser : true;
        const matchesAction = selectedAction ? log.action === selectedAction : true;
        const matchesFacility = true; // Show all logs returned by backend, don't filter by tenant

        const matchesStat = statFilter === 'ALL' ? true :
            statFilter === 'TODAY' ? log.actionTimestamp.startsWith(today) :
                statFilter === 'FAILED' ? !log.success : true;

        let matchesDate = true;
        if (dateFrom || dateTo) {
            const logTime = parseLogTime(log.createdAt || log.actionTimestamp);
            if (logTime > 0) {
                const logDateObj = new Date(logTime);
                const logDateLocal = new Date(logDateObj.getFullYear(), logDateObj.getMonth(), logDateObj.getDate());
                if (dateFrom) {
                    const fromLocal = new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate());
                    if (logDateLocal < fromLocal) matchesDate = false;
                }
                if (dateTo) {
                    const toLocal = new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate());
                    if (logDateLocal > toLocal) matchesDate = false;
                }
            }
        }

        return matchesUser && matchesAction && matchesFacility && matchesStat && matchesDate;
    });

    const displayedLogs = React.useMemo(() => {
        let logs = [...filteredLogs];
        if (dateFrom) {
            logs = logs.sort((a, b) => {
                const timeA = parseLogTime(a.createdAt || a.actionTimestamp);
                const timeB = parseLogTime(b.createdAt || b.actionTimestamp);
                return timeA - timeB;
            });
        }
        return logs;
    }, [filteredLogs, dateFrom, dateTo]);

    const paginatedLogs = displayedLogs.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    const activityStats = [
        { label: 'Total Actions', subLabel: '(Last 30 days)', value: todayActionsCount.toString(), icon: ActivityIcon, color: '#60a5fa', bgColor: '#eff6ff', filterValue: 'ALL' as const, clickable: true },
        { label: "Today's Action", value: todaysAuditCount.toString(), icon: HistoryIcon, color: '#22c55e', bgColor: '#ecfdf5', filterValue: 'TODAY' as const, clickable: true },
        { label: 'Total Users', value: uniqueUsersCount.toString(), icon: PersonIcon, color: '#4ade80', bgColor: '#f0fdf4', filterValue: 'USERS' as const, clickable: false },
        { label: 'Failed Attempts', value: failedAttemptsCount.toString(), icon: AlertCircleIcon, color: '#f87171', bgColor: '#fef2f2', filterValue: 'FAILED' as const, clickable: true },
    ];

    const uniqueUsers = Array.from(new Set([
        ...allUserNames,
        ...auditLogs.map(log => log.userName)
    ])).filter(Boolean).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    const uniqueActions = Array.from(new Set(auditLogs.map(log => log.action)));

    const getStatusColor = (success: boolean) => {
        return success ? 'success' : 'error';
    };

    const handleResetAll = () => {
        setSearchQuery('');
        setSelectedUser('');
        setSelectedAction('');
        setDateFrom(null);
        setDateTo(null);
        setStatFilter('ALL');
        setPage(1);
    };

    const handleExport = async () => {
        try {
            setExportLoading(true);
            const blob = await auditLogService.exportLogs();

            // Determine file extension from Content-Type if possible; default to .csv
            const mimeType = blob.type || 'text/csv';
            const ext = mimeType.includes('excel') || mimeType.includes('spreadsheet')
                ? 'xlsx'
                : mimeType.includes('json')
                    ? 'json'
                    : 'csv';

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.${ext}`;
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export audit logs:', err);
            setError('Failed to export audit logs. Please try again.');
        } finally {
            setExportLoading(false);
        }
    };


    return (
        <Box sx={{ pt: 3 }}>
            {/* ── Header ──────────────────────────────────────────────────── */}
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AuditLogSvgIcon size={40} color="#48CAE4" />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight="700" sx={{ mb: 0.5, color: '#1A2B45' }}>
                            Audit Logs
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem" }}>
                            Monitor system activities, user actions, and data changes for compliance
                        </Typography>
                    </Box>
                </Box>
                <Tooltip title="Export Audit Log">
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={exportLoading ? <CircularProgress size={16} sx={{ color: '#000' }} /> : <FileDownloadIcon />}
                        onClick={handleExport}
                        disabled={exportLoading}
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            bgcolor: '#48CAE4',
                            color: '#000000',
                            fontWeight: 'normal',
                            textTransform: 'none',
                            borderRadius: '10px',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: '#3ab3cc',
                                boxShadow: 'none'
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#48CAE4',
                                opacity: 0.7,
                                color: '#000000',
                            }
                        }}
                    >
                        {exportLoading ? 'Exporting...' : 'Export Logs'}
                    </Button>
                </Tooltip>
            </Box>

            {/* Activity Stats */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {activityStats.map((stat, index) => {
                    const Icon = stat.icon;
                    const isActive = statFilter === stat.filterValue;
                    const isClickable = (stat as any).clickable !== false;
                    return (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card
                                onClick={() => {
                                    if (isClickable) {
                                        setStatFilter((stat as any).filterValue);
                                    }
                                }}
                                sx={{
                                    p: 2.5,
                                    borderRadius: '16px',
                                    border: '1px solid',
                                    borderColor: isActive && isClickable ? stat.color : 'divider',
                                    background: '#fff',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    transition: 'all 0.2s ease-in-out',
                                    cursor: isClickable ? 'pointer' : 'default',
                                    '&:hover': isClickable ? {
                                        boxShadow: `0 4px 16px ${alpha(stat.color, 0.15)}`,
                                        borderColor: isActive ? stat.color : alpha(stat.color, 0.5)
                                    } : {}
                                }} elevation={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box
                                        sx={{
                                            p: 1.25,
                                            borderRadius: '12px',
                                            background: `${stat.color}20`,
                                            color: stat.color,
                                            display: 'flex',
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 28 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: stat.color, lineHeight: 1, fontSize: "1.75rem", mb: 0.5 }}>
                                            {stat.value}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: "0.82rem", whiteSpace: "nowrap", display: "block" }}>
                                            {stat.label}
                                        </Typography>
                                        {(stat as any).subLabel && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: "0.75rem", whiteSpace: "nowrap", display: "block" }}>
                                                {(stat as any).subLabel}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                    variant="outlined"
                    placeholder="Search logs"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        flex: 1,
                        minWidth: 250,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            backgroundColor: '#fff',
                            '&:hover': {
                                backgroundColor: '#fff',
                                borderColor: 'primary.main',
                            }
                        }
                    }}
                />

                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>All Users</InputLabel>
                    <Select
                        value={selectedUser}
                        label="All Users"
                        onChange={(e) => setSelectedUser(e.target.value)}
                        sx={{ borderRadius: '12px' }}
                        MenuProps={{
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                            PaperProps: { sx: { mt: '2px', maxHeight: 300 } },
                        }}
                    >
                        <MenuItem value="">All Users</MenuItem>
                        {uniqueUsers.map(user => (
                            <MenuItem key={user} value={user}>{user}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>All Actions</InputLabel>
                    <Select
                        value={selectedAction}
                        label="All Actions"
                        onChange={(e) => setSelectedAction(e.target.value)}
                        sx={{ borderRadius: '12px' }}
                        MenuProps={{
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                            PaperProps: { sx: { mt: '2px', maxHeight: 300 } },
                        }}
                    >
                        <MenuItem value="">All Actions</MenuItem>
                        {uniqueActions.map((action) => (
                            <MenuItem key={action} value={action}>
                                {action.replace(/_/g, ' ')}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="From Date"
                        value={dateFrom}
                        onChange={(newValue) => {
                            setDateFrom(newValue);
                            if (newValue && dateTo && dateTo < newValue) {
                                setDateTo(newValue);
                            }
                        }}
                        format="dd/MM/yyyy"
                        slotProps={{ textField: { sx: { width: 160, '& .MuiOutlinedInput-root': { borderRadius: '12px' } } } }}
                    />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="To Date"
                        value={dateTo}
                        minDate={dateFrom || undefined}
                        onChange={(newValue) => {
                            if (newValue && dateFrom && newValue < dateFrom) {
                                setDateTo(dateFrom);
                            } else {
                                setDateTo(newValue);
                            }
                        }}
                        format="dd/MM/yyyy"
                        slotProps={{ textField: { sx: { width: 160, '& .MuiOutlinedInput-root': { borderRadius: '12px' } } } }}
                    />
                </LocalizationProvider>

                <Tooltip title="Reset all filters">
                    <IconButton
                        onClick={handleResetAll}
                        size="medium"
                        sx={{
                            flexShrink: 0,
                            color: 'text.secondary',
                            transition: 'color 0.2s',
                            '&:hover': {
                                color: 'error.main',
                                bgcolor: 'transparent',
                            },
                        }}
                    >
                        <ResetIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Paper>

            {/* Audit Logs Table */}
            <TableContainer component={Paper} sx={{ mb: 3, overflowX: 'auto' }}>
                <Table sx={{ minWidth: 1000 }} aria-label="audit logs table">
                    <TableHead sx={{
                        "& .MuiTableCell-head": {
                            color: "#434445ff !important",
                        }
                    }}>
                        <TableRow>
                            <TableCell >Resident Name</TableCell>
                            <TableCell >Timestamp</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>Action</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>Entity Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>Role</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>Facility</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>IP Address</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : paginatedLogs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">No logs found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedLogs.map((log) => (
                                <TableRow
                                    key={log.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#f9fafb' } }}
                                >
                                    <TableCell sx={{ fontSize: '0.875rem' }}>
                                        {(!log.residentName || log.residentName === 'Unknown Resident') ? '-' : log.residentName}
                                    </TableCell>
                                    <TableCell component="th" scope="row" sx={{ fontSize: '0.875rem' }}>
                                        {robustFormat(log.createdAt || log.actionTimestamp)}
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight="medium">
                                                {log.userName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {log.userEmail}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.action.replace(/_/g, ' ')}
                                            color="primary"
                                            size="small"
                                            variant="filled"
                                            sx={{ fontSize: '0.75rem', fontWeight: 'bold', borderRadius: 1 }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.875rem' }}>
                                        {log.entityName || '-'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.875rem' }}>
                                        <RoleChip role={log.userRole?.replace(/,/g, ', ') || ''} />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.875rem' }}>
                                        {log.facilityName?.replace(/_/g, ' ') || 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.875rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                                        {log.ipAddress}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.success ? 'Success' : 'Failed'}
                                            color={getStatusColor(log.success)}
                                            size="small"
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'center' }, gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Showing {displayedLogs.length > 0 ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, displayedLogs.length)} of {displayedLogs.length} entries
                </Typography>
                <Pagination
                    count={Math.ceil(displayedLogs.length / rowsPerPage)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    variant="outlined"
                    shape="rounded"
                    size="small"
                />
            </Box>

            {/* Snackbar for export notification */}

        </Box>
    );
};

export default AuditLogsPage;
